import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { t } from "ttag";
import { z } from "zod";

import {
  CANCEL_SUBSCRIPTION_MODAL_STEPS,
  useCancelSubscriptionContext,
} from "@/features/billing/context";
import { useSendCancellationRequest } from "@/features/billing/hooks/use-send-cancellation-request";
import type { CancellationReason } from "@/features/billing/models";
import { Button, Checkbox, Dialog, Form } from "@/shared/ui";
import { useCancellationDiscount } from "../../hooks";

const formSchema = z.object({
  reasons: z.array(z.number()),
});

interface FeedbackSelectionProps {
  options: CancellationReason[];
}

export const CancellationReasonsSelection = ({
  options,
}: FeedbackSelectionProps) => {
  const sendCancellationRequest = useSendCancellationRequest();
  const {
    subscription,
    actions: { onClose, setReasons, setCurrentModal },
  } = useCancelSubscriptionContext();

  const { data: discount } = useCancellationDiscount({
    subscriptionId: subscription.id,
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reasons: [],
    },
  });

  const handleContinue = form.handleSubmit((values) => {
    const hasDiscountCredits = Boolean(discount?.credit);
    setReasons(values.reasons);

    if (hasDiscountCredits) {
      return setCurrentModal(CANCEL_SUBSCRIPTION_MODAL_STEPS.DISCOUNT);
    }

    sendCancellationRequest.mutate(subscription.id, {
      onSuccess: () =>
        setCurrentModal(CANCEL_SUBSCRIPTION_MODAL_STEPS.EMAIL_SENT),
    });
  });

  return (
    <Form.Provider {...form}>
      <form className="flex flex-col gap-6" onSubmit={handleContinue}>
        <fieldset className="flex flex-col gap-2 py-10">
          <legend className="sr-only">{t`Why are you cancelling?`}</legend>
          <Form.Field
            name="reasons"
            control={form.control}
            render={() => (
              <Form.Item>
                {options?.map((option) => (
                  <Form.Field
                    key={option.id}
                    control={form.control}
                    name="reasons"
                    render={({ field }) => {
                      return (
                        <Form.Item key={option.id}>
                          <Form.Control>
                            <Checkbox.Item
                              isCard
                              className="w-full"
                              checked={field.value?.includes(option.id)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...field.value, option.id])
                                  : field.onChange(
                                      field.value?.filter(
                                        (value) => value !== option.id,
                                      ),
                                    );
                              }}
                            >
                              <Checkbox.Label>{option.label}</Checkbox.Label>
                            </Checkbox.Item>
                          </Form.Control>
                        </Form.Item>
                      );
                    }}
                  />
                ))}
              </Form.Item>
            )}
          />
        </fieldset>

        <Dialog.Footer>
          <Button variant="secondary" size="lg" onClick={onClose}>
            {t`Go back`}
          </Button>
          <Button
            type="submit"
            size="lg"
            disabled={
              !form.getValues().reasons.length ||
              sendCancellationRequest.isPending
            }
          >
            {t`Continue`}
          </Button>
        </Dialog.Footer>
      </form>
    </Form.Provider>
  );
};
